import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, Archive, BarChart2, BarChart3, Bell, BookOpen, Calendar,
  CheckSquare, Eye, FlaskConical, HelpCircle, History, Layers, LineChart,
  Package, Search, Settings, SlidersHorizontal, Sprout, Thermometer,
  X, Inbox, Zap, type LucideIcon,
} from 'lucide-react';
import { useAppStore } from '../store';
import { navigationSchema, type NavItem } from '../config/navigationSchema';

const ICON_MAP: Record<string, LucideIcon> = {
  Activity, Archive, BarChart2, BarChart3, Bell, BookOpen, Calendar,
  CheckSquare, Eye, FlaskConical, History, Layers, LineChart,
  Package, Settings, SlidersHorizontal, Sprout, Thermometer, Zap,
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function CommandPalette({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  const overlayRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Reset state when opening/closing
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedIndex(0);
      // Autofocus input
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Click outside detection
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (overlayRef.current && e.target === overlayRef.current) {
      onClose();
    }
  };

  // Simple includes search on label and category
  const filteredItems = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return navigationSchema;
    return navigationSchema.filter(item => 
      item.label.toLowerCase().includes(q) || 
      item.category.toLowerCase().includes(q) ||
      item.path.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  // Handle index boundaries when list length changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredItems]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (filteredItems.length === 0 ? 0 : (prev + 1) % filteredItems.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (filteredItems.length === 0 ? 0 : (prev - 1 + filteredItems.length) % filteredItems.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredItems[selectedIndex]) {
        handleNavigate(filteredItems[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (resultsRef.current && filteredItems.length > 0) {
      const activeEl = resultsRef.current.children[selectedIndex] as HTMLElement;
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, filteredItems]);

  const handleNavigate = (item: NavItem) => {
    if (item.tabId) {
      navigate(`${item.path}?tab=${item.tabId}`);
    } else {
      navigate(item.path);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh] px-4 animate-backdrop"
      style={{
        background: 'rgba(5, 6, 8, 0.65)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div
        className="w-full max-w-[560px] rounded-2xl flex flex-col overflow-hidden animate-popup-desktop"
        style={{
          background: 'rgba(15, 17, 21, 0.85)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.45)',
          fontFamily: "'Geist', system-ui, sans-serif",
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Search Input Bar */}
        <div 
          className="flex items-center gap-3 px-4 py-3.5"
          style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}
        >
          <Search className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--text-4)' }} />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Aramak istediğiniz komutu veya sayfayı yazın..."
            className="w-full bg-transparent border-0 outline-none text-[14px] font-normal"
            style={{ 
              color: 'var(--text-primary)',
              caretColor: 'var(--ok)',
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-white/[0.05] transition-all cursor-pointer"
              style={{ color: 'var(--text-3)' }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Results Container */}
        <div 
          ref={resultsRef}
          className="max-h-[340px] overflow-y-auto py-2"
        >
          {filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <Inbox className="w-8 h-8 mb-2" style={{ color: 'var(--text-4)' }} />
              <p className="text-[13px]" style={{ color: 'var(--text-3)' }}>Eşleşen komut bulunamadı.</p>
            </div>
          ) : (
            filteredItems.map((item, index) => {
              const active = index === selectedIndex;
              return (
                <PaletteRow
                  key={item.id}
                  item={item}
                  active={active}
                  onClick={() => handleNavigate(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                />
              );
            })
          )}
        </div>

        {/* Footer shortcuts */}
        <div 
          className="flex items-center gap-4 px-4 py-3 text-[11px] mono tabular-nums"
          style={{ 
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
            color: 'var(--text-4)',
            background: 'rgba(0, 0, 0, 0.15)',
          }}
        >
          <div className="flex items-center gap-1">
            <span className="kbd text-[9px] px-1 py-0.5 rounded" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>↑↓</span>
            <span>Gezin</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="kbd text-[9px] px-1 py-0.5 rounded" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>enter</span>
            <span>Aç</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="kbd text-[9px] px-1 py-0.5 rounded" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>esc</span>
            <span>Kapat</span>
          </div>
          <div className="ml-auto" style={{ color: 'var(--text-4)' }}>
            Gurme Komuta
          </div>
        </div>
      </div>
    </div>
  );
}

interface RowProps {
  item: NavItem;
  active: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}

function PaletteRow({ item, active, onClick, onMouseEnter }: RowProps) {
  // Connect to Zustand store dynamically using the string badgeKey
  const badgeValue = useAppStore(useCallback((s: any) => {
    if (!item.badgeKey) return 0;
    return s[item.badgeKey] || 0;
  }, [item.badgeKey]));

  const Icon = ICON_MAP[item.icon] ?? HelpCircle;

  return (
    <div
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className="flex items-center gap-3 px-4 py-3 mx-2 rounded-xl transition-all cursor-pointer text-left"
      style={{
        background: active ? 'rgba(255, 255, 255, 0.04)' : 'transparent',
      }}
    >
      {/* 6px status dot if badgeKey value > 0 */}
      <div className="w-1.5 h-1.5 flex-shrink-0 flex items-center justify-center">
        {item.badgeKey && badgeValue > 0 && (
          <span 
            className="w-1.5 h-1.5 rounded-full animate-pulse" 
            style={{ background: 'var(--crit)' }} 
          />
        )}
      </div>

      {/* Lucide icon */}
      <Icon 
        className="w-4 h-4 flex-shrink-0 transition-colors" 
        style={{ color: active ? 'var(--ok)' : 'var(--text-3)' }} 
      />

      {/* Label */}
      <div className="flex-1 min-w-0 flex flex-col">
        <span
          className="font-semibold truncate transition-colors"
          style={{ fontSize: 'var(--text-nav)', color: active ? 'var(--text-primary)' : 'var(--text-2)' }}
        >
          {item.label}
        </span>
        {item.tabId && (
          <span className="text-[10px] mono" style={{ color: 'var(--text-4)' }}>
            ?tab={item.tabId}
          </span>
        )}
      </div>

      {/* Category and kbd badges */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span 
          className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
          style={{ 
            background: active ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.02)',
            color: 'var(--text-3)',
            border: '1px solid rgba(255, 255, 255, 0.04)',
          }}
        >
          {item.category}
        </span>
        {item.kbd && (
          <span className="kbd text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(255, 255, 255, 0.08)', color: 'var(--text-2)' }}>
            {item.kbd}
          </span>
        )}
      </div>
    </div>
  );
}
